import {useCallback, useState} from 'react';

// Chakra
import {
    Table,
    Tbody,
    Tr,
    Td,
    useColorModeValue,
    Thead, useDisclosure, Button, Text
} from '@chakra-ui/react';

import Card from 'components/Card/Card';
import WinnerEditModal from './WinnerEditModal';
import {DeleteIcon} from '@chakra-ui/icons';
import {apiService} from 'services/ApiService';
import Attention from 'components/Modals/Attention.js';
import {useQueryClient} from 'react-query';
import {hideString, parseDateTime} from 'services/Utils';
import usePermissionStatusByKey from '../../../../hooks/usePermissionStatusByKey';

export default function GiveawayWinners({
                                            slotMachineUrl,
                                            giveaway,
                                            hardShowWinners,
                                            isEditable = true,
                                            showPrizeOrdinal = true
                                        }) {
    const isUpdateGiveawayWinners = usePermissionStatusByKey('PATCH:/admin/winners/:id');
    const isDeleteGiveawayWinners = usePermissionStatusByKey('DELETE:/admin/winners/:id');

    const isRevealWinner = usePermissionStatusByKey('PATCH:/admin/winners/:id/visible');
    const isGiveawayGenerateWinners = usePermissionStatusByKey('POST:/admin/winners/generate/:giveawayId/all');
    const isGiveawayGenerateWinner = usePermissionStatusByKey('POST:/admin/winners/generate/:giveawayId');

    const isWheel = isRevealWinner && isGiveawayGenerateWinners && isGiveawayGenerateWinner;

    
    const queryClient = useQueryClient();
    const [selectedWinner, setSelectedWinner] = useState();
    const [deleteWinnerId, setDeleteWinnerId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const textColor = useColorModeValue('gray.700', 'white');

    const handleDeleteWinner = useCallback(async (id) => {
        await apiService.deleteGiveawayWinner({id});
        queryClient.invalidateQueries(['giveawayLogs']);
    }, [queryClient]);

    const handleRevealWinner = useCallback(async (id) => {
        await apiService.revealGiveawayWinner({id});
        queryClient.invalidateQueries(['giveawayWinners']);
    }, [queryClient]);

    const {isOpen, onOpen, onClose} = useDisclosure();

    const getOrdinalSuffix = useCallback((number) => {
        const lastDigit = number % 10;
        const lastTwoDigits = number % 100;

        if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
            return `${number}th`;
        }

        switch (lastDigit) {
            case 1:
                return `${number}st`;
            case 2:
                return `${number}nd`;
            case 3:
                return `${number}rd`;
            default:
                return `${number}th`;
        }
    }, []);

    const processWinnerData = useCallback(
        (text, isHidden) => {
            if (!isHidden) {
                return text;
            }
            if (!giveaway) {
                return;
            }
            const declareDate = new Date(giveaway.declareDate);

            const currentDate = new Date();

            const seconds = Math.ceil(
                Math.abs(declareDate.getTime() - currentDate.getTime()) /
                // (1000 * 3600 * 24)
                1000
            );

            if (seconds > 0 && text) {
                return '*'.repeat(text.length);
            } else {
                return text;
            }
        },
        [giveaway, hardShowWinners]
    );

    const goTo = useCallback(
        (startIndex, direction) => {
            const params = new URLSearchParams();
            params.set('startIndex', startIndex);
            params.set('direction', direction);
            params.set('showPrizeOrdinal', String(showPrizeOrdinal));
            window.open(
                `/#/game${slotMachineUrl}/${giveaway.id}?${params.toString()}`,
                '_blank'
            );
        },
        [giveaway, showPrizeOrdinal, slotMachineUrl]
    );

    return (
        <Card padding='24px' mb='40px'>
            <Table color={textColor}>
                <Thead>
                    <Tr>
                        <Td>Place</Td>
                        <Td>Title</Td>
                        <Td>Winner</Td>
                        {isWheel && (
                            <Td></Td>)}
                        {isDeleteGiveawayWinners && (
                            <Td></Td>
                        )}

                    </Tr>
                </Thead>
                <Tbody>
                    {giveaway.winners &&
                        giveaway.winners.map(({prize, user, place, id, packageNames, createdAt, isHidden}) => {
                            const firstName = prize?.winner?.firstName || user?.firstName;
                            const lastName = prize?.winner?.lastName || user?.lastName;
                            const fullName = `${firstName || ''} ${lastName || ''}`.trim();
                            const maskedName = hideString(fullName || '?', !isHidden);
                            let nameWarning = null;
                            if (!firstName && !lastName) {
                                nameWarning = '(winner is missing first name and last name, please verify)';
                            } else if (!firstName) {
                                nameWarning = '(winner is missing first name, please verify)';
                            } else if (!lastName) {
                                nameWarning = '(winner is missing last name, please verify)';
                            }

                            return (
                                <Tr key={prize.index}>
                                    <Td>{getOrdinalSuffix(place)}</Td>
                                    <Td>{prize.title}</Td>
                                    <Td onClick={() => {
                                        if (isEditable && isUpdateGiveawayWinners) {
                                            setSelectedWinner({
                                                user: {
                                                    firstName: prize?.winner?.firstName || user?.firstName,
                                                    lastName: prize?.winner?.lastName || user?.lastName,
                                                    email: prize?.winner?.email || user?.email
                                                },
                                                prizeTitle: prize?.title,
                                                id
                                            });
                                        }
                                    }}
                                        cursor={isEditable && isUpdateGiveawayWinners ? 'pointer' : 'unset'}
                                        _hover={isEditable && isUpdateGiveawayWinners ? {textDecoration: 'underline'} : {}}>
                                        <p>
                                            Name: {maskedName}
                                            {isHidden && nameWarning && (
                                                <Text as='span' color='orange.300' fontSize='sm' ml='6px'>
                                                    {nameWarning}
                                                </Text>
                                            )}
                                        </p>
                                        <Text fontSize="md" letterSpacing="wide"
                                              color='white'>{`Phone: ${hideString(user?.phone, !isHidden, 3, true)}`}  </Text>
                                        <p>Email: {hideString(user?.email, !isHidden)}</p>
                                        <p>Generated at: {parseDateTime(createdAt)}</p>
                                        <p>Package: {packageNames?.[0]}</p>

                                    </Td>
                                    {isWheel && (
                                        <Td>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'flex-start',
                                                }}>
                                                <Button
                                                    variant='ghost'
                                                    onClick={() => goTo(place - 1, 'down')}>
                                                    Reveal → Top
                                                </Button>
                                                <Button
                                                    variant='ghost'
                                                    onClick={() => goTo(place - 1, 'up')}>
                                                    Reveal → Bottom
                                                </Button>
                                            </div>
                                        </Td>)}
                                    {isDeleteGiveawayWinners && (
                                        <Td>
                                            <Button isDisabled={giveaway?.status === 'finished' || !isEditable}
                                                    onClick={() => {
                                                        setDeleteWinnerId(id);
                                                        onOpen();
                                                    }}
                                                    variant='ghost'>
                                                <DeleteIcon
                                                    color='#9FABCB'
                                                    boxSize={5}
                                                />
                                            </Button>
                                        </Td>
                                    )}

                                </Tr>
                            );
                        })}
                    <Tr></Tr>
                </Tbody>
            </Table>
            <Attention
                variant="remove"
                isLoading={isDeleting}
                handle={async () => {
                    if (isDeleting || deleteWinnerId === null) {
                        return;
                    }
                    setIsDeleting(true);
                    try {
                        await handleDeleteWinner(deleteWinnerId);
                        queryClient.invalidateQueries(['giveawayWinners']);
                    } finally {
                        setIsDeleting(false);
                        onClose();
                        setDeleteWinnerId(null);
                    }
                }}
                isOpen={isOpen}
                onOpen={onOpen}
                onClose={() => {
                    if (isDeleting) {
                        return;
                    }
                    onClose();
                    setDeleteWinnerId(null);
                }}
            />
            <WinnerEditModal id={selectedWinner?.id} isOpen={selectedWinner} onClose={() => setSelectedWinner(null)}
                             initialValues={selectedWinner?.user} prizeTitle={selectedWinner?.prizeTitle}/>
        </Card>
    );
}
